import React from 'react';
import { Share2, Leaf, Clock, User, Check, MapPin, DollarSign } from 'lucide-react';
import { ClassData, SignupData } from '../../types';
import { formatDate, formatTime, dateToKey } from '../../helpers';

interface DayViewProps {
    selectedDay: Date;
    classesForSelectedDay: ClassData[];
    signupsByClassId: Record<string, SignupData[]>;
    getUserStatus: (cls: ClassData) => 'booked' | null;
    openShareModal: (type: 'date' | 'class', data: string, name?: string) => void;
    nextUpcomingClass: ClassData | null;
    setSelectedDay: (date: Date) => void;
    handleBookingStart: (cls: ClassData) => void;
    onManageBooking: (cls: ClassData) => void; // Replaces part of handleBookingStart/click logic
}

const DayView: React.FC<DayViewProps> = ({ 
    selectedDay, 
    classesForSelectedDay, 
    signupsByClassId, 
    getUserStatus, 
    openShareModal, 
    nextUpcomingClass, 
    setSelectedDay,
    handleBookingStart,
    onManageBooking
}) => {
    return (
        <div className="space-y-4 h-full">
            <div className="mb-2 flex items-center justify-between border-b border-white/10 pb-4 shrink-0">
            <div>
                <h2 className="text-3xl md:text-4xl font-serif text-transparent bg-clip-text bg-gradient-to-r from-slate-100 to-slate-400">
                    {selectedDay.toLocaleDateString('en-US', { weekday: 'long' })}
                </h2>
                <p className="text-rose-400/80 font-medium">{selectedDay.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</p>
            </div>
            <button 
                onClick={() => openShareModal('date', dateToKey(selectedDay), `${formatDate(selectedDay)}`)} 
                className="text-slate-500 hover:text-fuchsia-400 transition-colors bg-white/5 p-2 rounded-full hover:bg-white/10"
            >
                <Share2 size={20} />
            </button>
            </div>
            
            {classesForSelectedDay.length === 0 && (
                <div className="py-16 text-center text-slate-500 border-2 border-dashed border-white/10 rounded-xl flex flex-col items-center justify-center gap-2">
                    <div className="bg-slate-800/50 p-4 rounded-full mb-2"><Leaf size={24} className="text-slate-600"/></div>
                    <p>No classes scheduled for this day.</p>
                    {/* Quick jump back to next class if empty */}
                    {nextUpcomingClass && (
                        <button onClick={() => setSelectedDay(new Date(nextUpcomingClass.date + 'T12:00:00'))} className="text-fuchsia-400 hover:text-fuchsia-300 text-sm hover:underline mt-2">
                            Jump to next class: {formatDate(new Date(nextUpcomingClass.date + 'T12:00:00'))}
                        </button>
                    )}
                </div>
            )}
            
            {classesForSelectedDay.map((cls) => {
            const attendees = signupsByClassId[cls.id] || [];
            const count = attendees.length;
            const status = { count, full: count >= (Number(cls.capacity) || 15), remaining: (Number(cls.capacity) || 15) - count };
            const userState = getUserStatus(cls);
            
            return (
                <div key={cls.id} className="bg-slate-900 rounded-xl p-6 shadow-xl border border-white/5 relative overflow-hidden group hover:border-fuchsia-500/30 transition-all shrink-0">
                
                {/* Signed Up Badge */}
                {userState === 'booked' && (
                    <div className="absolute top-0 right-0 bg-emerald-600 text-white text-xs px-3 py-1 rounded-bl-lg font-bold z-10 shadow-lg">
                        <Check size={12} className="inline mr-1"/>Signed Up
                    </div>
                )}

                <button 
                    onClick={(e) => { e.stopPropagation(); openShareModal('class', cls.id, cls.name); }}
                    className={`absolute right-4 p-2 text-slate-500 hover:text-fuchsia-400 transition-colors rounded-full hover:bg-white/10 z-10 ${userState === 'booked' ? 'top-8' : 'top-4'}`}
                >
                    <Share2 size={20} />
                </button>

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="pr-12 md:pr-0">
                        <div className="flex items-center gap-2 text-rose-400 font-bold mb-1 tracking-wider text-xs uppercase">
                        <Clock size={14} /><span>{formatTime(cls.startTime)} - {formatTime(cls.endTime)}</span>
                        </div>
                        <h3 className="text-2xl font-bold text-slate-100 mb-2 font-serif">
                            <button 
                            onClick={() => userState === 'booked' ? onManageBooking(cls) : handleBookingStart(cls)}
                            disabled={status.full && userState !== 'booked'}
                            className={`text-left transition-colors ${status.full && userState !== 'booked' ? 'cursor-default opacity-50' : 'hover:text-fuchsia-400 cursor-pointer'}`}
                            >
                            {cls.name}
                            </button>
                        </h3>
                        <div className="text-slate-400 text-sm flex flex-wrap gap-4 items-center">
                            <span className="flex items-center gap-1"><User size={14} className="text-slate-500"/> {cls.instructor}</span>
                            <span className="flex items-center gap-1"><MapPin size={14} className="text-slate-500"/> {cls.studio || 'N/A'}</span>
                            {cls.paymentType === 'venmo' && (
                            <span className="text-emerald-400 font-bold bg-emerald-900/20 px-2 py-0.5 rounded text-xs border border-emerald-900/50 inline-flex items-center">
                                {cls.cost ? (
                                    <>
                                        <DollarSign size={12} className="mr-0.5"/>
                                        {cls.cost}
                                    </>
                                ) : (
                                    "Pay What You Can"
                                )}
                            </span>
                            )}
                        </div>
                        {cls.description && (
                        <div className="mt-4 text-sm text-slate-300 border-t border-white/5 pt-3 leading-relaxed whitespace-pre-wrap">
                            {cls.description}
                        </div>
                        )}
                    </div>
                    
                    <div className="flex flex-row md:flex-col items-center justify-between md:justify-center gap-4 min-w-[120px]">
                    <div className="text-right md:text-center">
                        <div className="text-[10px] text-slate-500 uppercase tracking-widest hidden md:block mb-1">Space</div>
                        <div className={`font-mono text-sm md:text-lg font-bold ${status.full ? 'text-rose-500' : 'text-emerald-400'}`}>
                            {status.full ? 'FULL' : `${status.remaining} left`}
                        </div>
                    </div>
                    
                    <button onClick={() => userState === 'booked' ? onManageBooking(cls) : handleBookingStart(cls)}
                        disabled={status.full && userState !== 'booked'}
                        className={`w-full px-6 py-3 rounded-lg font-bold shadow-lg transition-all transform active:scale-95 
                        ${userState === 'booked' 
                            ? 'bg-slate-700 text-slate-300 border border-slate-600' 
                            : 'bg-gradient-to-r from-rose-600 to-fuchsia-700 text-white hover:from-rose-500 hover:to-fuchsia-600 shadow-rose-900/20'
                        }`}>
                        {userState === 'booked' ? 'Manage' : 'Register'}
                    </button>
                    </div>
                </div>
                </div>
            );
            })}
        </div>
    );
};

export default React.memo(DayView);
